<template>
  <div>
    <div class="tab-body__grid">
      <ContactDialog
        v-if="modalVisible"
        @contactUpdated="contactModified"
        @closeModal="modalVisible = false"
      />
    </div>
    <div class="tab-body__header">
      <div v-if="facilities && selectedFacilities" class="selected-facilities">
        <Multiselect
          :options="facilities"
          width="500px"
          v-model="selectedFacilities"
          :multiple="true"
          placeholder="Choose one or more facilities"
          :searchable="false"
          :close-on-select="false"
          track-by="id"
          label="title"
          deselect-label="Click to deselect"
        >
          <template #option="props">
            <div>
              <label>
                <input :checked="props.option.selected" type="checkbox" />
              </label>
              <span>{{ props.option.title }}</span>
            </div>
          </template>
          <template v-slot:selection>
            <div
              v-if="selectedFacilities.length"
              class="multiselect__tags-wrap"
            >
              <span class="multiselect__tag">
                <span>
                  {{ selectedFacilities.filter((f) => f.id != allId).length }} /
                  {{ facilities.length - 1 }} Facilities selected</span
                >
                <i
                  class="multiselect__tag-icon"
                  @click="selectedFacilities = []"
                ></i>
              </span>
            </div>
          </template>
        </Multiselect>
      </div>
      <Button
        class="new-contact-button"
        type="button"
        themeColor="success"
        @click="modalVisible = true"
        >Add New Contact</Button
      >
    </div>

    <div v-if="sortedSelectedFacilities && facilities && dataRolesForUser">
      <FacilityContactGrid
        v-for="facility of sortedSelectedFacilities"
        :key="facility.id"
        :isCopacker="facility.IsCopacker"
        :isReadOnly="isReadOnly"
        :id="facility.id"
        :name="getFacilityTitle(facility.id)"
        :isShared="facilities.find((x) => x.id === facility.id)?.IsShared"
        :type="facility.type"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { api } from "@/api/";
import Multiselect from "vue-multiselect";
import FacilityContactGrid from "./FacilityContactGrid.vue";
import ContactDialog from "./ContactDialog.vue";
import type { Contact } from "@/Domain/Contact";
import { useMainStore } from "@/stores/useMainStore";
import { UserRoles } from "@/Domain/UserRoles";
import { Button } from "@progress/kendo-vue-buttons";
import { EnumFacilityType } from "@/Domain/EnumFacilityType";

interface Facility {
  IsShared: boolean;
  id: number;
  title: string;
  IsCopacker: boolean;
  selected: boolean;
  type: EnumFacilityType;
}

const main = useMainStore();
let companyLogin = main.companyLogin;
const refreshByFacilityContactsCounter = main.refreshByFacilityContactsCounter;

const allId = -2;
const allTitle = "All";

const facilities = ref<Facility[]>([]); // Has two reserved slots. facilities[0] is always "ALL", facilities[1] is the Main Office if the user is entitled to it
const modalVisible = ref(false);
const selectedFacilities = ref<Facility[]>([]);
const checkAlLChecked = ref(0);
const checkAlLUnChecked = ref(0);
const dataRolesForUser = ref<string[]>();
const isReadOnly = ref(true);

const sortedSelectedFacilities = computed(() => {
  // show all selected facilities except the first one ( which is the "Check All" ) and sort them by title with "Main Office" always first
  const sortedList = selectedFacilities.value
    .filter((x) => x.id != allId && !x.title.startsWith("Main Office"))
    .sort((a, b) => (a.title < b.title ? -1 : 1));

  if (
    facilities.value &&
    facilities.value.length > 1 &&
    facilities.value[1].title.startsWith("Main Office") &&
    selectedFacilities.value.some((f) => f.title.startsWith("Main Office"))
  )
    sortedList.unshift(facilities.value[1]); // always insert Main Office as the first selected facility

  return sortedList;
});

function getFacilityTitle(id: number): string {
  const facility = facilities.value.find((f) => f.id === id);
  if (!facility) return "No Title";
  else return facility.title;
}

function hasRole(appRoles: string[]) {
  let ret = false;

  if (dataRolesForUser.value)
    dataRolesForUser.value.forEach((x) =>
      appRoles.forEach((y) => {
        if (x == y) ret = true;
      })
    );

  return ret;
}

async function GetFacilitiesAndUserRoles(keepSelectedFacilities: boolean) {
  api
    .get("GetRolesForUser", {})
    .then((resp) => {
      if (resp.success) {
        dataRolesForUser.value = resp.response;

        if (
          !hasRole(UserRoles.ROLES_ALL_RFR) ||
          hasRole(UserRoles.ROLES_ALL_RC) ||
          hasRole(UserRoles.ROLES_ALL_INTERNAL_STAFF)
        )
          // if user is RFR and not an RC or another internal staff role, then it's an RFR. And disable the contact updates
          isReadOnly.value = false;

        api
          .get("GetFacilities", {
            userLogin: main.userLogin,
            companyLogin: main.companyLogin,
            includeMainOffice: !isReadOnly.value, // RFR is not entitled to see the main office contacts (if he doesnt have another internal staff role)
            subApplication: 8,
            contactId: -1,
            includeSpecial: false,
          })
          .then((resp) => {
            if (resp.success) {
              const result = resp.response.map((x) => ({
                title: `${x.Name} [${x.StatusAsString}]`,
                id: x.Id,
                IsShared: x.IsShared,
                IsCopacker: x.IsCopacker,
                selected: false,
                type: x.FacilityType!,
              }));

              facilities.value.length = 0;
              result.forEach((x) => facilities.value.push(x)); // The first facility returned by the server is always the Main Office if the user is entitled to view it.
              if (facilities.value.length > 0) {
                facilities.value.unshift({
                  IsShared: false,
                  id: allId,
                  title: allTitle,
                  IsCopacker: false,
                  selected: false,
                  type: EnumFacilityType.Plant,
                }); // Insert "All" checkbox as first facility in the list so now facilities[0] is "ALL"
                if (!keepSelectedFacilities) {
                  selectedFacilities.value.length = 0;
                  if (facilities.value.length > 1) {
                    facilities.value[1].selected = true;
                    selectedFacilities.value.push(facilities.value[1]); // If user is entitled to Main Office, default to just it. Otherwise, default to first facility
                  }
                } else
                  facilities.value.forEach(
                    (facility) =>
                      (facility.selected =
                        selectedFacilities.value.find(
                          (x) => x.id == facility.id
                        ) != undefined)
                  );
              }
            } else {
              console.error(
                "ByFacility.vue: GetFacilities(): resp = " + resp
              );
            }
          })
          .catch((err) => {
            console.error(
              "ByFacility.vue: GetFacilities(): catch error = " + err
            );
          });
      } else
        console.error(
          "ByFacility.vue: GetRolesForUser(): resp = " + resp
        );
    })
    .catch((err) => {
      console.error("ByFacility.vue: GetRolesForUser(): catch error = " + err);
    });
}

GetFacilitiesAndUserRoles(false);

function contactModified(c: Contact | undefined, error?: string) {
  modalVisible.value = false;

  if (c) main.refreshByFacilityContactsCounter++; // this will cause the facility contacts grids to be updated
}

watch(selectedFacilities, (selectedFacilities) => {
  console.debug("ByFacility.vue: watch(): selectedFacilities changed");
  if (facilities.value) {
    // The code below is a bit tricky.
    // When the user selects / deselects the first option (All) we need to populate "selectedFields" with all / none of the facilities.
    // When we do that, we are changing selectedFacilites which triggers a recursive call to this watch.
    const checkAllBefore =
      facilities.value.length > 0 && facilities.value[0].selected; // the value of the All before we record the user's choice in facilities

    // Now record the user's choice in facilities
    facilities.value.forEach(
      (facility) =>
        (facility.selected =
          selectedFacilities.find((x) => x.id == facility.id) != undefined)
    ); // Whenever a facility is selected or deselected, set the property "selected" of the corresponding facility in "selectedFacilities" so it can be used in the slot

    // Now that we have recorded the user's choice to "facilities", determine whether the first one (All) has been changed (either selected or deselected).
    if (
      !checkAllBefore &&
      facilities.value.length > 0 &&
      facilities.value[0].selected
    )
      checkAlLChecked.value++;
    else if (
      checkAllBefore &&
      facilities.value.length > 0 &&
      !facilities.value[0].selected
    )
      checkAlLUnChecked.value++;
    else {
      // else nothing happens and hence we don't have any recursion problems.
    }
  }
});

watch(checkAlLChecked, () => {
  // Handle "Check All"
  console.debug("ByFacility.vue: watch(): CheckAll checked");
  selectedFacilities.value.length = 0;
  facilities.value.forEach((x) => {
    x.selected = true;
    selectedFacilities.value.push(x);
  });
});
watch(checkAlLUnChecked, () => {
  // Handle "Check All"
  console.debug("ByFacility.vue: watch(): checkAlLUnChecked checked");
  selectedFacilities.value = [];
  facilities.value.forEach((x) => (x.selected = false));
});

// "main" can be changed in one of 3 ways. 1. A new company is being emulated. 2. The company hasn't changed, but a contact has been added or modified. 3. The session timeout counter has been reset
// In the third case we don't want to do anything.
watch(main, () => {
  console.debug("ByFacility.vue: watch(): main changed");
  let restoreSelectedFacilities = true; // the add/modify contact modal incremented main.refreshCounter to indicate the faciltiy-contact-grids need to be refreshed.
  if (main.companyLogin != companyLogin) {
    console.debug(
      "ByFacility.vue: watch(): company being emulated has changed. Setting companyLogin = " +
        main.companyLogin
    );
    companyLogin = main.companyLogin;
    restoreSelectedFacilities = false; // this is a new company being emulated so we don't want to save the selecteFacilities since the new company will have different facilities
    GetFacilitiesAndUserRoles(restoreSelectedFacilities);
  } else if (
    main.refreshByFacilityContactsCounter != refreshByFacilityContactsCounter
  ) {
    console.debug(
      "ByFacility.vue: watch(): refreshByFacilityContactsCounter has changed to = " +
        refreshByFacilityContactsCounter +
        ", main.refreshByFacilityContactsCounter = " +
        main.refreshByFacilityContactsCounter
    );
    GetFacilitiesAndUserRoles(restoreSelectedFacilities);
  }
});
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
<style scoped lang="scss">
.tab-body__header {
  background-color: #ffffff;
  padding: 10px;
  border: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .new-contact-button {
    height: 40px;
  }
}
.selected-facilities {
  width: 450px;
}
.selected-facilities :deep(*) {
  font-size: 13px;
}
</style>
